import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Cog8ToothIcon,
  CheckCircleIcon,
  BoltIcon,
  LightBulbIcon,
  WrenchScrewdriverIcon,
  Square3Stack3DIcon,
  CubeIcon,
  VideoCameraIcon,
  AdjustmentsHorizontalIcon,
  CursorArrowRaysIcon,
  PlusIcon,
  FolderIcon,
  WindowIcon
} from '@heroicons/react/24/outline';

const tasks = [
    {
      title: 'AutoCAD Blueprint',
      instruction: 'Zgjidhni artikullin e saktë për: "Lötkolben" (Maja e saldimit)',
      options: ['Der Lötkolben', 'Die Lötkolben', 'Das Lötkolben'],
      correct: 'Der Lötkolben',
      partNumber: 'MK-901-焊接',
      explanation: 'Fjalët që përfundojnë me "-en" si "der Kolben" janë zakonisht të gjinisë mashkullore.'
    },
    {
      title: 'Inventor Component',
      instruction: 'Si thuhet "Kushineta" në gjermanisht?',
      options: ['Das Kugellager', 'Der Motor', 'Die Achse'],
      correct: 'Das Kugellager',
      partNumber: 'MK-SKF-2026',
      explanation: '"Kugellager" do të thotë kushinetë. "Kugel" = sferë, "Lager" = mbajtëse.'
    },
    {
      title: 'Technical Drawing',
      instruction: 'Si thuhet "Vizatimi teknik" në gjermanisht?',
      options: ['Die technische Zeichnung', 'Der technischer Plan', 'Das technisches Bild'],
      correct: 'Die technische Zeichnung',
      partNumber: 'DWG-04-A3',
      explanation: '"Zeichnung" është gjinia femërore (Die Zeichnung). Është baza e punës në CAD.'
    },
    {
      title: 'Machine Logic',
      instruction: 'Zgjidhni përkthimin për: "Mirëmbajtja e makinës"',
      options: ['Maschinenwartung', 'Maschinenbau', 'Maschinenschnitt'],
      correct: 'Maschinenwartung',
      partNumber: 'MNT-SYS-V1',
      explanation: '"Wartung" do të thotë mirëmbajtje/shërbim teknik.'
    },
    {
      title: 'Electrical Circuit',
      instruction: 'Si thuhet "Qarku elektrik" në gjermanisht?',
      options: ['Stromkreis', 'Spannung', 'Widerstand'],
      correct: 'Stromkreis',
      partNumber: 'CIRC-X-230',
      explanation: '"Strom" është rryma, "Kreis" është qarku.'
    },
    {
      title: 'Hydraulic System',
      instruction: 'Zgjidhni artikullin e saktë për: "Druckventil" (Valvula e presionit)',
      options: ['Das Druckventil', 'Der Druckventil', 'Die Druckventil'],
      correct: 'Das Druckventil',
      partNumber: 'HYD-VALV-300',
      explanation: 'Fjalët e përbëra që mbarojnë me "das Ventil" marrin gjininë asnjëanëse (Das Druckventil).'
    },
    {
      title: 'Sensor Technology',
      instruction: 'Si përkthehet fjala "Der Näherungssensor" në shqip?',
      options: ['Sensori i afërsisë', 'Sensori i temperaturës', 'Sensori i dritës'],
      correct: 'Sensori i afërsisë',
      partNumber: 'SNS-PROX-B2',
      explanation: '"Näherung" vjen nga "nähern" (afrohem). Është sensori i afërsisë që dallon objektet afër pa kontakt fizik.'
    },
    {
      title: 'Control Logic (PLC)',
      instruction: 'Çfarë do të thotë shkurtesa gjermane "SPS" (Speicherprogrammierbare Steuerung) në shqip?',
      options: ['PLC (Kontrollues Logjik i Programueshëm)', 'Sensori i Sigurisë', 'Qarku me Relé'],
      correct: 'PLC (Kontrollues Logjik i Programueshëm)',
      partNumber: 'PLC-SIEMENS-S7',
      explanation: '"Speicherprogrammierbare Steuerung" (SPS) është termi gjerman për PLC.'
    },
    {
      title: 'Mechanical Engineering',
      instruction: 'Si përkthehet fjala "Die Getriebewelle" në shqip?',
      options: ['Boshti i kutisë së marsheve', 'Pompë hidraulike', 'Cilindri pneumatik'],
      correct: 'Boshti i kutisë së marsheve',
      partNumber: 'SHAFT-GB-012',
      explanation: '"Welle" është boshti (shaft) dhe "Getriebe" është kutia e marsheve (gearbox).'
    },
    {
      title: 'Safety Measures',
      instruction: 'Si thuhet "Çelësi i emergjencës" (Emergency Stop) në gjermanisht?',
      options: ['Der Not-Aus-Schalter', 'Der Hauptschalter', 'Der Sicherheitsschalter'],
      correct: 'Der Not-Aus-Schalter',
      partNumber: 'SAFE-STOP-911',
      explanation: '"Not" = emergjencë, "Aus" = fikje, "Schalter" = ndërprerës/çelës. Përdoret për fikje të menjëhershme të makinerisë në raste rreziku.'
    }
];

const MechatronicsGame = () => {
  const [shuffledTasks, setShuffledTasks] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const { user, updateUserStats } = useAuth();

  useEffect(() => {
    setShuffledTasks([...tasks].sort(() => Math.random() - 0.5));
  }, []);

  const handleSelect = (option) => {
    if (selectedOption !== null) return;
    setSelectedOption(option);
    if (option === shuffledTasks[currentIdx].correct) {
      setScore(score + 10);
    }
    setShowExplanation(true);
  };

  const finishGame = async () => {
    const totalXp = (user?.xp || 0) + score;
    await updateUserStats({ xp: totalXp });
    setCompleted(true);
  };

  const handleNext = () => {
    if (currentIdx < shuffledTasks.length - 1) {
      setCurrentIdx(currentIdx + 1);
      setSelectedOption(null);
      setShowExplanation(false);
    } else {
      finishGame();
    }
  };

  if (shuffledTasks.length === 0) return null;

  if (completed) {
    return (
      <div className="bg-[#f5f5f5] p-12 rounded-xl text-center shadow-2xl border border-gray-300 font-sans">
        <div className="w-20 h-20 bg-brand-primary/15 rounded-xl flex items-center justify-center mx-auto mb-6 text-brand-primary border border-brand-primary/20">
          <CheckCircleIcon className="w-12 h-12" />
        </div>
        <h2 className="text-2xl font-black text-brand-text mb-2 uppercase tracking-tighter italic">Render Finished</h2>
        <p className="text-brand-text/60 mb-10 text-[10px] font-bold uppercase tracking-widest">Status: 100% Complete | XP Gained: {score}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-12 py-3 bg-[#0696d7] text-white rounded-sm font-bold hover:bg-[#057ab0] transition-all active:scale-95 shadow-md uppercase text-xs tracking-widest"
        >
          Finish & Return
        </button>
      </div>
    );
  }

  const currentTask = shuffledTasks[currentIdx];

  return (
    <div className="bg-[#f0f0f0] rounded-sm overflow-hidden shadow-2xl border border-[#c5c5c5] flex flex-col font-sans relative min-h-[650px] select-none text-brand-text">
      
      {/* 1. TOP RIBBON (INVENTOR LIGHT MODE) */}
      <div className="bg-[#e2e2e2] flex flex-col border-b border-[#bcbcbc]">
        {/* Title Bar */}
        <div className="h-8 flex items-center px-4 justify-between bg-[#ffffff] border-b border-[#d5d5d5]">
          <div className="flex items-center gap-2 text-[10px] text-brand-text/70 font-medium">
             <Square3Stack3DIcon className="w-3.5 h-3.5 text-[#0696d7]" />
             <span className="font-semibold">Autodesk Inventor Professional 2026 - German_Assembly.iam</span>
          </div>
          <div className="flex items-center gap-1">
             <div className="flex">
                <div className="w-10 h-8 flex items-center justify-center hover:bg-brand-primary/10 cursor-pointer text-brand-text/70">_</div>
                <div className="w-10 h-8 flex items-center justify-center hover:bg-brand-primary/10 cursor-pointer text-brand-text/70">□</div>
                <div className="w-10 h-8 flex items-center justify-center hover:bg-red-500 hover:text-white cursor-pointer text-brand-text/70">×</div>
             </div>
          </div>
        </div>

        {/* Tab Strip */}
        <div className="flex px-4 gap-1 py-0.5 text-[11px] font-medium text-brand-text/80 bg-[#e2e2e2]">
           <span className="bg-[#f0f0f0] px-4 py-1.5 border-t border-x border-[#bcbcbc] rounded-t-sm cursor-pointer relative top-[1px]">Assemble</span>
           <span className="hover:bg-[#d5d5d5] px-4 py-1.5 transition-colors cursor-pointer">Design</span>
           <span className="hover:bg-[#d5d5d5] px-4 py-1.5 transition-colors cursor-pointer">Sketch</span>
           <span className="hover:bg-[#d5d5d5] px-4 py-1.5 transition-colors cursor-pointer">Inspect</span>
           <span className="hover:bg-[#d5d5d5] px-4 py-1.5 transition-colors cursor-pointer">Tools</span>
        </div>

        {/* Ribbon Tools Container */}
        <div className="p-1 px-2 flex gap-0.5 items-stretch bg-[#f0f0f0] h-20 border-b border-[#bcbcbc]">
           <div className="flex flex-col items-center justify-between px-3 border-r border-[#dcdcdc] group hover:bg-[#e5e5e5] cursor-pointer py-1">
              <PlusIcon className="w-8 h-8 text-[#555555] group-hover:text-[#0696d7]" />
              <span className="text-[9px] text-brand-text/70">Place</span>
           </div>
           <div className="flex flex-col items-center justify-between px-3 border-r border-[#dcdcdc] group hover:bg-[#e5e5e5] cursor-pointer py-1">
              <WrenchScrewdriverIcon className="w-8 h-8 text-[#555555] group-hover:text-[#0696d7]" />
              <span className="text-[9px] text-brand-text/70">Joint</span>
           </div>
           <div className="flex flex-col items-center justify-between px-3 border-r border-[#dcdcdc] group hover:bg-[#e5e5e5] cursor-pointer py-1 text-[#f27d07]">
              <AdjustmentsHorizontalIcon className="w-8 h-8" />
              <span className="text-[9px]">Constrain</span>
           </div>
           <div className="flex flex-col items-center justify-between px-4 border-r border-[#dcdcdc] group hover:bg-[#e5e5e5] cursor-pointer py-1">
              <div className="grid grid-cols-2 gap-0.5">
                 <div className="w-3 h-3 bg-gray-400"></div>
                 <div className="w-3 h-3 bg-gray-400"></div>
                 <div className="w-3 h-3 bg-gray-400"></div>
                 <div className="w-3 h-3 bg-gray-400"></div>
              </div>
              <span className="text-[9px] text-brand-text/70 mt-1">Pattern</span>
           </div>
           <div className="flex flex-col items-center justify-between px-4 group hover:bg-[#e5e5e5] cursor-pointer py-1">
              <BoltIcon className="w-8 h-8 text-[#f27d07]" />
              <span className="text-[9px] text-[#f27d07] font-bold">Analyze</span>
           </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        
        {/* 2. MODEL BROWSER (LEFT SIDE - LIGHT MODE) */}
        <div className="w-60 bg-[#ffffff] border-r border-[#bcbcbc] flex flex-col shadow-inner">
           <div className="px-3 py-1.5 bg-[#f5f5f5] border-b border-[#bcbcbc] flex items-center justify-between">
              <span className="text-[10px] font-bold text-brand-text/80 uppercase italic">Model Browser</span>
              <div className="flex gap-1">
                 <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                 <XMarkIcon className="w-2.5 h-2.5 text-brand-text/50" />
              </div>
           </div>
           <div className="flex-1 p-2 text-[11px] text-brand-text/80 space-y-1 overflow-y-auto">
              <div className="flex items-center gap-1.5 font-bold">
                 <Square3Stack3DIcon className="w-3.5 h-3.5 text-[#f27d07]" />
                 <span>German_Assembly.iam</span>
              </div>
              <div className="pl-4 space-y-1">
                 <div className="flex items-center gap-1.5 hover:bg-[#e8f3ff] cursor-pointer px-1">
                    <FolderIcon className="w-3.5 h-3.5 text-[#f2b707]" />
                    <span>Relationships</span>
                 </div>
                 <div className="flex items-center gap-1.5 hover:bg-[#e8f3ff] cursor-pointer px-1">
                    <WindowIcon className="w-3.5 h-3.5 text-brand-primary" />
                    <span>Representations</span>
                 </div>
                 <div className="flex items-center gap-1.5 hover:bg-[#e8f3ff] cursor-pointer px-1">
                    <CubeIcon className="w-3.5 h-3.5 text-brand-primary" />
                    <span>Origin</span>
                 </div>
                 <div className="flex items-center gap-1.5 bg-[#007acc] text-white px-1 py-0.5 mt-2 rounded-sm ring-1 ring-[#005a9e]">
                    <Cog8ToothIcon className="w-3.5 h-3.5" />
                    <span>Task_{currentIdx + 1}:Active</span>
                 </div>
                 {shuffledTasks.slice(currentIdx + 1).map((_, i) => (
                   <div key={i} className="flex items-center gap-1.5 text-brand-text/50 px-1 py-0.5">
                      <Cog8ToothIcon className="w-3.5 h-3.5" />
                      <span>Task_{currentIdx + i + 2}</span>
                   </div>
                 ))}
              </div>
           </div>
           {/* Bottom Info Bar */}
           <div className="p-3 bg-[#f5f5f5] border-t border-[#bcbcbc]">
              <div className="flex justify-between items-center text-[10px] font-bold px-1">
                 <span className="text-brand-text/60">SESSION XP:</span>
                 <span className="text-[#0696d7]">{score} pts</span>
              </div>
           </div>
        </div>

        {/* 3. VIEWPORT (MAIN AREA - LIGHT MODE GRADIENT) */}
        <div className="flex-1 relative bg-gradient-to-br from-[#c0d8f0] via-[#ebf3fa] to-[#ffffff] overflow-hidden">
           
           {/* Navigation Cube Overlay */}
           <div className="absolute top-6 right-6 w-16 h-16 bg-[#fdfdfd] border border-gray-300 rounded shadow-md flex items-center justify-center text-[8px] font-black text-brand-text/50 select-none group cursor-pointer hover:border-[#0696d7] transition-all z-20">
              <div className="w-full h-full relative border border-brand-secondary/15 p-0.5">
                <div className="w-full h-full bg-[#f0f0f0] border border-brand-secondary/20 flex items-center justify-center">TOP</div>
              </div>
           </div>

           {/* Viewport Toolbar */}
           <div className="absolute left-4 top-1/2 -translate-y-1/2 flex flex-col gap-1.5 z-20">
              <div className="p-1.5 bg-white/80 border border-gray-300 rounded shadow-sm hover:bg-brand-primary/10 cursor-pointer"><CursorArrowRaysIcon className="w-4 h-4 text-brand-text/80" /></div>
              <div className="p-1.5 bg-white/80 border border-gray-300 rounded shadow-sm hover:bg-brand-primary/10 cursor-pointer"><VideoCameraIcon className="w-4 h-4 text-brand-text/80" /></div>
              <div className="p-1.5 bg-white/80 border border-gray-300 rounded shadow-sm hover:bg-brand-primary/10 cursor-pointer"><AdjustmentsHorizontalIcon className="w-4 h-4 text-brand-text/80" /></div>
           </div>

           {/* 4. DIALOG PANEL (INVENTOR STYLE IN LIGHT MODE) */}
           <div className="absolute inset-0 flex items-center justify-center p-8 z-10">
              <div className="bg-[#f0f0f0] w-full max-w-lg border border-[#bcbcbc] shadow-[0_20px_50px_rgba(0,0,0,0.15)] rounded-sm flex flex-col overflow-hidden">
                 
                 {/* Dialog Title Bar */}
                 <div className="h-7 bg-[#ffffff] flex items-center justify-between px-3 border-b border-[#bcbcbc]">
                    <span className="text-[11px] font-semibold text-brand-text/80">Properties: Task Parameters</span>
                    <div className="flex gap-1">
                      <div className="w-5 h-5 flex items-center justify-center hover:bg-brand-primary/10 cursor-pointer text-brand-text/60">?</div>
                      <div className="w-5 h-5 flex items-center justify-center hover:bg-red-500 hover:text-white cursor-pointer text-brand-text/60">×</div>
                    </div>
                 </div>

                 {/* Tab Content Area */}
                 <div className="p-5">
                    <div className="bg-[#ffffff] border border-[#bcbcbc] rounded-sm p-5 shadow-inner">
                       <div className="mb-6">
                          <div className="flex items-center gap-2 mb-3">
                             <span className="text-[9px] text-[#0696d7] font-bold uppercase tracking-tight whitespace-nowrap">Input Requirements</span>
                             <div className="flex-1 h-px bg-[#d5d5d5]"></div>
                          </div>
                          <h2 className="text-[15px] font-bold text-brand-text mb-1 tracking-tight leading-snug">{currentTask.instruction}</h2>
                          <div className="text-[9px] text-brand-text/60 font-mono mt-1">Component_ID: {currentTask.partNumber}</div>
                       </div>

                       {/* Interaction Rows */}
                       <div className="space-y-1.5">
                          {currentTask.options.map((opt, i) => {
                             const isCorrect = opt === currentTask.correct;
                             const isSelected = selectedOption === opt;

                             let rowStyle = "bg-[#f9f9f9] border-[#d5d5d5] text-brand-text/80 hover:bg-[#eef5fd] hover:border-[#b8d1f3]";
                             if (selectedOption) {
                                if (isCorrect) rowStyle = "bg-[#e7f6e7] border-[#4caf50] text-[#2e7d32]";
                                else if (isSelected) rowStyle = "bg-[#feebee] border-[#ef5350] text-[#c62828]";
                                else rowStyle = "bg-[#f5f5f5] border-[#e0e0e0] text-brand-text/50 opacity-60";
                             }

                             return (
                               <button
                                 key={i}
                                 onClick={() => handleSelect(opt)}
                                 disabled={selectedOption !== null}
                                 className={`w-full p-2.5 px-4 text-left border rounded-sm transition-all flex items-center justify-between group ${rowStyle}`}
                               >
                                  <div className="flex items-center gap-3">
                                     <div className={`w-3.5 h-3.5 rounded-full border border-gray-400 flex items-center justify-center bg-white`}>
                                        {isSelected && <div className={`w-2 h-2 rounded-full ${isCorrect ? 'bg-[#4caf50]' : 'bg-[#ef5350]'}`}></div>}
                                     </div>
                                     <span className="text-[12px] font-medium tracking-tight">{opt}</span>
                                  </div>
                                  <span className="text-[9px] text-brand-text/50 font-mono opacity-0 group-hover:opacity-100 transition-opacity">0{i+1}_PAR</span>
                               </button>
                             );
                          })}
                       </div>
                    </div>

                    {/* Expandable Explanation Panel */}
                    <div className={`mt-4 overflow-hidden transition-all duration-300 ${showExplanation ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}>
                       <div className="bg-[#fff9e6] p-4 border border-[#ffe082] rounded-sm flex gap-3 mb-4">
                          <LightBulbIcon className="w-5 h-5 text-[#f2b707] flex-shrink-0" />
                          <div className="flex flex-col">
                             <span className="text-[9px] text-[#a67c00] font-bold uppercase tracking-widest mb-1">Knowledge Transfer</span>
                             <p className="text-[11px] text-[#7a5c00] leading-normal font-medium italic">
                               {currentTask.explanation}
                             </p>
                          </div>
                       </div>
                       
                       <div className="flex justify-end gap-2">
                          <button
                            onClick={handleNext}
                            className="px-6 py-2 bg-[#0696d7] hover:bg-[#057ab0] text-white text-[11px] font-bold rounded-sm shadow-sm transition-all active:translate-y-0.5"
                          >
                            Apply & Next
                          </button>
                       </div>
                    </div>
                 </div>
              </div>
           </div>

           {/* Floating Info Panel */}
           <div className="absolute bottom-4 right-4 bg-white/70 backdrop-blur-sm px-4 py-2 border border-gray-300 shadow-sm flex flex-col items-end">
              <span className="text-[9px] font-bold text-brand-text/60 uppercase tracking-tighter">Drafting Environment</span>
              <div className="w-24 h-[1px] bg-gray-300 my-1"></div>
              <span className="text-[8px] font-mono text-brand-text/50">FPS: 60 | COORDS: AUTO</span>
           </div>

        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
        ::-webkit-scrollbar { height: 6px; width: 6px; }
        ::-webkit-scrollbar-thumb { background: #ccc; border-radius: 3px; }
        ::-webkit-scrollbar-track { background: #f0f0f0; }
        * { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; }
      `}} />
    </div>
  );
};

const XMarkIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default MechatronicsGame;
